/* eslint-disable arrow-body-style */
import React, { useEffect, useMemo } from 'react';
import _ from 'lodash';
import { object } from 'prop-types';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { buttonLengthConstants, buttonVariantConstants } from '../../../common/constants';
import { useRootState } from '../../../context/root-state.context';
import Row from '../../FlexboxGrid/Row.jsx';
import Col from '../../FlexboxGrid/Col.jsx';
import HorizontalScroller from '../Carousels/HorizontalScroller';
import LooxRating from '../../Product/LooxRating';
import LooxWidgetScript from '../../Product/LooxWidgetScript';
import { renderRichTextReact } from '../../../utils/storyblokRichText';
import { getStoryblokAspectRatio } from '../../../utils/storyblokAsset';
import ButtonRegular from '../../Button/ButtonRegular.jsx';
import ProductVariantSelector from './ProductVariantSelector.jsx';

// eslint-disable-next-line arrow-body-style
const TwoColumnProductHighlight = ({ blok }) => {
  const { setShopifyProduct } = useRootState();

  // query product variants from Shopify
  const productVariantsQuery = useStaticQuery(graphql`
    {
      allShopifyProductVariant {
        edges {
          node {
            product {
              handle
              shopifyId
              storefrontId
              title
            }
            price
            storefrontId
            title
          }
        }
      }
    }
  `);

  // filter product variants by the storefrontId from SB
  const selectedProductVariant = useMemo(() => {
    const variantStorefrontID = blok.productVariant;

    if (!_.isEmpty(productVariantsQuery.allShopifyProductVariant)) {
      const { node } = productVariantsQuery.allShopifyProductVariant.edges
        .filter((edge) => {
          return edge.node.storefrontId === variantStorefrontID;
        })
        .find((edge) => edge.node);

      return node;
    }

    return null;
  }, [blok, productVariantsQuery]);

  const productTitleModified = useMemo(() => {
    const title = _.get(selectedProductVariant, 'product.title', '');

    if (!title.includes('Grogu')) {
      return blok.productPageTitle; // Return the title as is if it doesn't include "Grogu"
    }

    // If the title includes "Grogu", wrap "mini" in <i></i>
    const parts = title.split(/(mini)/i); // Split the title into parts, keeping "mini" as a separate element
    // eslint-disable-next-line react/no-array-index-key
    return <>{parts.map((part, index) => (part.toLowerCase() === 'mini' ? <i key={index}>{part}</i> : part))}</>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductVariant]);

  // set variant color to Star Wars for ProductVariantSelector bg image
  useEffect(() => {
    if (blok.theme && selectedProductVariant) {
      if (blok.theme === 'starWars') {
        selectedProductVariant.color = 'starWars';
      }
    }
  }, [blok, selectedProductVariant]);

  useEffect(() => {
    setShopifyProduct(selectedProductVariant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductVariant]);

  const logoAsset = blok?.logo?.find((el) => el)?.assetLg;

  return (
    <>
      <Row className="max-w-[100%]">
        {/* left column */}
        <Col xs={8} md={6} lg={7}>
          {!_.isEmpty(blok.columnLeftGallery) && (
            <HorizontalScroller images={blok.columnLeftGallery} imageWidth="380px" />
          )}
        </Col>

        {/* right column */}
        <Col xs={8} md={3} lg={4} className="md:ml-[10%] lg:ml-[8.333333333333334%] relative">
          {/* absolutely positioned logo */}
          {selectedProductVariant && !_.isEmpty(blok.logo) && (
            <div className="mt-[30px] md:mt-[0] mb-[10px]">
              <img
                alt={logoAsset?.alt}
                className="w-[80px] sm:w-[100px] md:w-[110px] lg:w-[150px]"
                style={{ aspectRatio: getStoryblokAspectRatio(logoAsset) }}
                src={logoAsset?.filename}
              />
            </div>
          )}

          {/* product name */}
          {selectedProductVariant && (
            <h2 className="typography-body-xxl font-bold text-beige-100">{productTitleModified}</h2>
          )}

          {/* product price */}
          {selectedProductVariant && (
            <p className="typography-body-xxl font-bold text-beige-100">
              ${selectedProductVariant.price.toLocaleString()}
            </p>
          )}

          {/* product variant selector */}
          {selectedProductVariant && (
            <div className="mt-[24px]">
              <ProductVariantSelector
                currentVariant={selectedProductVariant}
                productVariants={[selectedProductVariant]}
              />
            </div>
          )}

          {/* variant name */}
          {selectedProductVariant && (
            <h2 className="typography-eyebrow-xs text-beige-100">{selectedProductVariant.title}</h2>
          )}

          {/* product description */}
          <div className="py-[30px] typography-body-l !font-utopiaStdCaption">
            {blok.description && renderRichTextReact(blok.description)}
          </div>

          {/* button */}
          {blok.linkText && blok.linkURL && (
            <div>
              <ButtonRegular
                length={buttonLengthConstants.NORMAL}
                onClick={() => navigate(blok.linkURL)}
                theme={blok.theme}
                variant={buttonVariantConstants.PRIMARY_ORANGE}
              >
                {blok.linkText}
              </ButtonRegular>
            </div>
          )}

          {/* product review */}
          {selectedProductVariant && blok.displayRating && (
            <div>
              <LooxRating link={blok.linkURL} shopifyId={selectedProductVariant.product?.shopifyId} />
            </div>
          )}
        </Col>
      </Row>
      <LooxWidgetScript />
    </>
  );
};

TwoColumnProductHighlight.propTypes = {
  blok: object.isRequired,
};

export default TwoColumnProductHighlight;
